import { apiClient, isApiError } from "@/lib/apiClient";
import { migrateThemeTokens } from "@/lib/migrateThemeTokens";
import type { Folder } from "@/lib/types";
import { validateProject, type ValidationReport } from "@kitsulab/schema";
import {
	useCallback,
	useDeferredValue,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
	type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import {
	ProjectDataContext,
	toProjectView,
	type Bundle,
	type BundleLoadStatus,
	type EntityCollectionKey,
	type EntityFor,
	type ProjectData,
	type ProjectTheme,
} from "./useProjectData";

const fetchBundle = async (slug: string): Promise<Bundle> => {
	const { data } = await apiClient.get<Bundle>(`/api/projects/${slug}/bundle`);
	return data;
};

interface ProviderProps {
	slug: string;
	children: ReactNode;
}

export function ProjectDataProvider({ slug, children }: ProviderProps) {
	const { t } = useTranslation();
	const [bundle, setBundle] = useState<Bundle | null>(null);
	const [status, setStatus] = useState<BundleLoadStatus>("loading");

	useEffect(() => {
		let cancelled = false;
		setBundle(null);
		setStatus("loading");
		void fetchBundle(slug)
			.then((b) => {
				if (cancelled) return;
				setBundle(b);
				setStatus("ready");
			})
			.catch((error: unknown) => {
				if (cancelled) return;
				setBundle(null);
				if (isApiError(error) && error.response?.status === 404) {
					setStatus("not-found");
					return;
				}
				setStatus("error");
			});
		return () => {
			cancelled = true;
		};
	}, [slug]);

	const reload = useCallback(async () => {
		setStatus("loading");
		try {
			const b = await fetchBundle(slug);
			setBundle(b);
			setStatus("ready");
		} catch (error) {
			setBundle(null);
			if (isApiError(error) && error.response?.status === 404) {
				setStatus("not-found");
				return;
			}
			setStatus("error");
			throw error;
		}
	}, [slug]);

	const upsert = useCallback(
		<K extends EntityCollectionKey>(kind: K, entity: EntityFor<K>) => {
			window.dispatchEvent(new Event("kitsulab:onboarding-update"));
			setBundle((prev) => {
				if (!prev) return prev;
				const list = prev[kind] as EntityFor<K>[];
				const idx = list.findIndex((e) => e.id === entity.id);
				const next =
					idx === -1 ? [...list, entity] : list.map((e, i) => (i === idx ? entity : e));
				return { ...prev, [kind]: next } as Bundle;
			});
		},
		[],
	);

	const upsertMany = useCallback(
		<K extends EntityCollectionKey>(kind: K, entities: EntityFor<K>[]) => {
			setBundle((prev) => {
				if (!prev) return prev;
				return { ...prev, [kind]: entities } as Bundle;
			});
		},
		[],
	);

	const remove = useCallback((kind: EntityCollectionKey, id: string) => {
		setBundle((prev) => {
			if (!prev) return prev;
			const list = prev[kind] as { id: string }[];
			return { ...prev, [kind]: list.filter((e) => e.id !== id) } as Bundle;
		});
	}, []);

	const setFolders = useCallback((folders: SetStateAction<Folder[]>) => {
		setBundle((prev) => {
			if (!prev) return prev;
			const next =
				typeof folders === "function"
					? (folders as (current: Folder[]) => Folder[])(prev.folders)
					: folders;
			return { ...prev, folders: next };
		});
	}, []);

	const setTheme = useCallback((theme: ProjectTheme) => {
		setBundle((prev) => (prev ? { ...prev, theme } : prev));
	}, []);

	const deferredBundle = useDeferredValue(bundle);

	const report = useMemo<ValidationReport | null>(() => {
		if (!deferredBundle) return null;
		return validateProject(toProjectView(deferredBundle), deferredBundle.config);
	}, [deferredBundle]);

	const value = useMemo<ProjectData | null>(() => {
		if (!bundle || !report) return null;
		return {
			slug,
			project: bundle.project,
			items: bundle.items,
			attacks: bundle.attacks,
			recipes: bundle.recipes,
			currencies: bundle.currencies,
			stats: bundle.stats,
			quests: bundle.quests,
			folders: bundle.folders,
			places: bundle.places,
			uiTrees: bundle.uiTrees,
			theme: bundle.theme
				? { tokens: migrateThemeTokens(bundle.theme.tokens) }
				: null,
			config: bundle.config,
			report,
			reload,
			upsert,
			upsertMany,
			remove,
			setFolders,
			setTheme,
		};
	}, [
		bundle,
		report,
		slug,
		reload,
		upsert,
		upsertMany,
		remove,
		setFolders,
		setTheme,
	]);

	if (!value) {
		const message =
			status === "not-found"
				? t("projectData.notFound")
				: status === "error"
					? t("projectData.loadError")
					: t("common.loading");
		return (
			<div
				className="flex flex-col items-center justify-center h-full gap-3"
				style={{ color: "#71717a", fontSize: 13 }}
			>
				<span>{message}</span>
				{status === "error" && (
					<button
						type="button"
						className="btn-pill btn-pill-primary"
						onClick={() => void reload()}
					>
						{t("common.retry")}
					</button>
				)}
			</div>
		);
	}

	return (
		<ProjectDataContext.Provider value={value}>
			{children}
		</ProjectDataContext.Provider>
	);
}
